// Data tables for the world: ground types, objects, rings. One place to tune. Items: items.ts.
import type { ItemId } from './items.ts';
import { CROPS, cropObj } from './farming.ts';

export const TILE = 48;
export const MAP_W = 1024;
export const MAP_H = 1024;
export const CHUNK = 32;
export const CHUNKS_X = MAP_W / CHUNK;
export const CHUNKS_Y = MAP_H / CHUNK;
/** Chunks around the player's chunk that the client is sent (radius). */
export const VIEW_CHUNKS = 2;
export const WORLD_SEED = 20261001;
export const MAX_PLAYERS = 6;
export const PLAYER_SPEED = 210; // px/s, ~4.4 tiles/s
export const PLAYER_RADIUS = 12;
export const SWING_TIME = 0.7; // s per harvest swing
export const REACH = TILE * 1.3; // centre-to-centre reach for harvesting

// ---------------------------------------------------------------- ground

export const G = {
  GRASS: 0,
  MEADOW: 1,
  DIRT: 2,
  FEN: 3,
  MUD: 4,
  HIGH: 5,
  ROCK: 6,
  ASH: 7,
  CINDER: 8,
  STONE: 9,
  SAND: 10,
  WATER: 11,
  DEEP: 12,
  SHALLOW: 13,
  // Dungeons (shared/dungeons.ts)
  BARROW: 14,
  CRYPT: 15,
  DEEP_MINE: 16,
  HELL_FLOOR: 17,
  CAVE_WALL: 18,
  // Portal realms (stage 8)
  HOLLOW: 19,
  GLADE: 20,
  THICKET: 21,
} as const;
export type GroundId = (typeof G)[keyof typeof G];

export const GROUNDS: { id: number; name: string; solid: boolean; color: number }[] = [
  { id: G.GRASS, name: 'grass', solid: false, color: 0x4f7a32 },
  { id: G.MEADOW, name: 'meadow', solid: false, color: 0x6c9a3a },
  { id: G.DIRT, name: 'dirt', solid: false, color: 0x7a5a36 },
  { id: G.FEN, name: 'fen', solid: false, color: 0x3d5a34 },
  { id: G.MUD, name: 'mud', solid: false, color: 0x4e3d2a },
  { id: G.HIGH, name: 'highland', solid: false, color: 0x6f7550 },
  { id: G.ROCK, name: 'rock', solid: false, color: 0x5d5d5d },
  { id: G.ASH, name: 'ash', solid: false, color: 0x4a4440 },
  { id: G.CINDER, name: 'cinder', solid: false, color: 0x6a3a2a },
  { id: G.STONE, name: 'stone floor', solid: false, color: 0x8a8478 },
  { id: G.SAND, name: 'sand', solid: false, color: 0xc8b07a },
  { id: G.WATER, name: 'water', solid: true, color: 0x2f5f86 },
  { id: G.DEEP, name: 'deep water', solid: true, color: 0x1d3c5c },
  { id: G.SHALLOW, name: 'shallows', solid: false, color: 0x4a7f9a },
  { id: G.BARROW, name: 'barrow floor', solid: false, color: 0x5a4a3a },
  { id: G.CRYPT, name: 'crypt floor', solid: false, color: 0x3a4a40 },
  { id: G.DEEP_MINE, name: 'mine floor', solid: false, color: 0x4a4a4e },
  { id: G.HELL_FLOOR, name: 'hell floor', solid: false, color: 0x5a2a2a },
  { id: G.CAVE_WALL, name: 'cave wall', solid: true, color: 0x1a1816 },
  { id: G.HOLLOW, name: 'hollow floor', solid: false, color: 0x4a4e56 },
  { id: G.GLADE, name: 'glade grass', solid: false, color: 0x3a4a5a },
  { id: G.THICKET, name: 'thicket', solid: true, color: 0x14201c },
];

export const groundSolid = (g: number): boolean => GROUNDS[g]?.solid ?? true;

// ---------------------------------------------------------------- objects

export type ObjKind = 'tree' | 'stump' | 'rock' | 'ore' | 'bush' | 'decor' | 'ruin' | 'log' | 'placed' | 'crop';

export type ObjDef = {
  id: number;
  key: string; // art group in public/art/objects.json
  kind: ObjKind;
  solid: boolean;
  /** Swing power needed to harvest; 0 = not harvestable. */
  hits: number;
  /** Tool that harvests it: 'axe' (hands work too, at half power), 'pick' (required), 'any'. */
  tool?: 'axe' | 'pick' | 'any';
  /** Minimum tool tier (stone 1, bronze 2, iron 3). */
  tier?: number;
  drops?: [ItemId, number][];
  /** What the tile becomes when harvested (0 = nothing). */
  after: number;
  /** World-clock seconds until the original object comes back; 0 = never. */
  regrow: number;
  color: number; // procedural fallback
  /** Gives off light at night (radius in px). */
  light?: number;
  /** Pick-only things that bare hands can still work at half power (so a new player can get stone). */
  hand?: boolean;
  /** Grows into another object after this many world seconds (crops). */
  grow?: { next: number; secs: number };
  /** Drawn over a tilled-soil patch (crops). */
  soil?: boolean;
  /** Harvesting drops seeds for this ring's crops (wild plants). */
  seeds?: boolean;
};

type Extra = Partial<Pick<ObjDef, 'tool' | 'tier' | 'drops' | 'light' | 'hand' | 'grow' | 'soil' | 'seeds'>>;
const O = (id: number, key: string, kind: ObjKind, solid: boolean, hits: number, after: number, regrow: number, color: number, extra: Extra = {}): ObjDef => ({ id, key, kind, solid, hits, after, regrow, color, ...extra });

export const OBJ = {
  NONE: 0,
  TREE: 1,
  PINE: 2,
  BIRCH: 3,
  FEN_TREE: 4,
  BURNT_TREE: 5,
  STUMP: 6,
  FEN_STUMP: 7,
  BURNT_STUMP: 8,
  BERRY_BUSH: 9,
  BARE_BUSH: 10,
  ROCK: 11,
  COPPER: 12,
  TIN: 13,
  IRON: 14,
  COAL: 15,
  SILVER: 16,
  GOLD: 17,
  DARK_ORE: 18,
  OBSIDIAN: 19,
  DECOR_GREEN: 20,
  DECOR_FEN: 21,
  DECOR_HIGH: 22,
  DECOR_ASH: 23,
  RUIN_WALL: 24,
  RUIN_PILLAR: 25,
  RUBBLE: 26,
  LOG: 27,
  BUSH: 28,
  BANNER: 29,
  STONE_WALL: 30,
  CHEST: 31,
  TORCH: 32,
  SOIL: 33,
  COOKING_FIRE: 34,
  FURNACE: 35,
  GENERATOR: 36,
  SAG_MILL: 37,
  ALLOY: 38,
  CONDUIT: 39,
  // 40-71: crops (shared/farming.ts)
  BLOOD_ALTAR: 72,
  BLOOD_RUNE: 73,
  RITUAL_STONE: 74,
  BLOOD_LAMP: 75,
  STORAGE_CORE: 76,
  TERMINAL: 77,
  BRAZIER: 78,
  PORTAL: 79,
  CABLE: 80,
  ENCHANT_BENCH: 81,
  STILL: 82,
  RESIN_TAP: 83,
  HARVESTER: 84,
  WATER_WHEEL: 85,
  WINDMILL: 86,
  ALTERNATOR: 87,
  SAPLING: 88,
  BLOOD_CAPSTONE: 89,
} as const;

const axe = (tier: number, drops: [ItemId, number][]): Extra => ({ tool: 'axe', tier, drops });
const pick = (tier: number, drops: [ItemId, number][]): Extra => ({ tool: 'pick', tier, drops });

export const OBJECTS: ObjDef[] = [];
const def = (d: ObjDef) => (OBJECTS[d.id] = d);
def(O(OBJ.NONE, '', 'decor', false, 0, 0, 0, 0));
def(O(OBJ.TREE, 'tree', 'tree', true, 4, OBJ.STUMP, 1200, 0x2f6a2a, axe(1, [['wood', 3]])));
def(O(OBJ.PINE, 'pine', 'tree', true, 4, OBJ.STUMP, 1200, 0x1f4f32, axe(1, [['wood', 3]])));
def(O(OBJ.BIRCH, 'birch', 'tree', true, 3, OBJ.STUMP, 1200, 0x8aa860, axe(1, [['wood', 2]])));
def(O(OBJ.FEN_TREE, 'fen_tree', 'tree', true, 5, OBJ.FEN_STUMP, 1500, 0x4a3050, axe(1, [['darkwood', 3]])));
def(O(OBJ.BURNT_TREE, 'burnt_tree', 'tree', true, 6, OBJ.BURNT_STUMP, 1800, 0x302826, axe(2, [['charwood', 3]])));
def(O(OBJ.STUMP, 'stump', 'stump', false, 0, 0, 0, 0x6a4a2a));
def(O(OBJ.FEN_STUMP, 'fen_stump', 'stump', false, 0, 0, 0, 0x4a3a3a));
def(O(OBJ.BURNT_STUMP, 'burnt_stump', 'stump', false, 0, 0, 0, 0x2a2220));
def(O(OBJ.BERRY_BUSH, 'berry_bush', 'bush', true, 1, OBJ.BARE_BUSH, 600, 0x3a7a3a, { tool: 'any', drops: [['berries', 3]] }));
def(O(OBJ.BARE_BUSH, 'bush', 'decor', true, 0, 0, 0, 0x3a6a2a));
def(O(OBJ.ROCK, 'rock', 'rock', true, 4, 0, 2400, 0x7a7a7a, { ...pick(1, [['stone', 3]]), hand: true }));
def(O(OBJ.COPPER, 'copper', 'ore', true, 5, 0, 3600, 0xc87a46, pick(1, [['copper_ore', 2], ['stone', 1]])));
def(O(OBJ.TIN, 'tin', 'ore', true, 5, 0, 3600, 0xa9b8c0, pick(1, [['tin_ore', 2], ['stone', 1]])));
def(O(OBJ.IRON, 'iron', 'ore', true, 6, 0, 3600, 0x8a6a5a, pick(2, [['iron_ore', 2], ['stone', 1]])));
def(O(OBJ.COAL, 'coal', 'ore', true, 5, 0, 3600, 0x2a2a2a, pick(2, [['coal', 3]])));
def(O(OBJ.SILVER, 'silver', 'ore', true, 7, 0, 4800, 0xd8dde2, pick(3, [['silver_ore', 2], ['stone', 1]])));
def(O(OBJ.GOLD, 'gold', 'ore', true, 7, 0, 4800, 0xe8c040, pick(3, [['gold_ore', 2], ['stone', 1]])));
def(O(OBJ.DARK_ORE, 'dark_ore', 'ore', true, 9, 0, 6000, 0x7a2a5a, pick(4, [['dark_ore', 2]])));
def(O(OBJ.OBSIDIAN, 'obsidian', 'ore', true, 9, 0, 6000, 0x2a1a3a, pick(4, [['obsidian', 2]])));
def(O(OBJ.DECOR_GREEN, 'decor_green', 'decor', false, 1, 0, 900, 0x5a9a3a, { tool: 'any', seeds: true }));
def(O(OBJ.DECOR_FEN, 'decor_fen', 'decor', false, 1, 0, 900, 0x6a4a7a, { tool: 'any', seeds: true }));
def(O(OBJ.DECOR_HIGH, 'decor_high', 'decor', false, 1, 0, 900, 0x8a8a6a, { tool: 'any', seeds: true }));
def(O(OBJ.DECOR_ASH, 'decor_ash', 'decor', false, 1, 0, 900, 0x5a4040, { tool: 'any', seeds: true }));
def(O(OBJ.RUIN_WALL, 'ruin_wall', 'ruin', true, 0, 0, 0, 0x8a8478));
def(O(OBJ.RUIN_PILLAR, 'ruin_pillar', 'ruin', true, 0, 0, 0, 0x9a9488));
def(O(OBJ.RUBBLE, 'rubble', 'decor', false, 2, 0, 0, 0x7a746a, { ...pick(1, [['stone', 2]]), hand: true }));
def(O(OBJ.LOG, 'log', 'log', true, 3, 0, 0, 0x6a4a2a, axe(1, [['wood', 2]])));
def(O(OBJ.BUSH, 'bush', 'decor', true, 0, 0, 0, 0x3a6a2a));
// Placed by players; picking them up gives the item back.
def(O(OBJ.BANNER, 'banner', 'placed', true, 2, 0, 0, 0x3a5aa0, { tool: 'any', drops: [['claim_banner', 1]], light: 120 }));
def(O(OBJ.STONE_WALL, 'stone_wall', 'placed', true, 3, 0, 0, 0x9a9488, pick(1, [['stone_wall', 1]])));
def(O(OBJ.CHEST, 'chest', 'placed', true, 2, 0, 0, 0x8a5a2a, { tool: 'any', drops: [['chest', 1]] }));
def(O(OBJ.TORCH, 'torch', 'placed', false, 1, 0, 0, 0xf0a040, { tool: 'any', drops: [['torch', 1]], light: 200 }));
def(O(OBJ.SOIL, 'soil', 'placed', false, 0, 0, 0, 0x5a3a20));
def(O(OBJ.FURNACE, 'furnace', 'placed', true, 2, 0, 0, 0x8a6a5a, { tool: 'any', drops: [['stone_furnace', 1]], light: 110 }));
def(O(OBJ.GENERATOR, 'generator', 'placed', true, 2, 0, 0, 0x8a7a5a, { tool: 'any', drops: [['generator', 1]] }));
def(O(OBJ.SAG_MILL, 'sag_mill', 'placed', true, 2, 0, 0, 0x7a7a8a, { tool: 'any', drops: [['sag_mill', 1]] }));
def(O(OBJ.ALLOY, 'alloy', 'placed', true, 2, 0, 0, 0xc05a2a, { tool: 'any', drops: [['alloy_smelter', 1]], light: 130 }));
def(O(OBJ.CONDUIT, 'conduit', 'placed', false, 1, 0, 0, 0xc8804a, { tool: 'any', drops: [['conduit', 1]] })); // the item pipe
def(O(OBJ.CABLE, 'cable', 'placed', false, 1, 0, 0, 0x5a6a7a, { tool: 'any', drops: [['cable', 1]] }));
def(O(OBJ.ENCHANT_BENCH, 'enchant_bench', 'placed', true, 2, 0, 0, 0x6a4a8a, { tool: 'any', drops: [['enchant_bench', 1]], light: 80 }));
def(O(OBJ.STILL, 'still', 'placed', true, 2, 0, 0, 0xc08a40, { tool: 'any', drops: [['still', 1]] }));
def(O(OBJ.RESIN_TAP, 'resin_tap', 'placed', false, 1, 0, 0, 0x8a6a3a, { tool: 'any', drops: [['resin_tap', 1]] }));
def(O(OBJ.HARVESTER, 'harvester', 'placed', true, 2, 0, 0, 0xa08040, { tool: 'any', drops: [['harvester', 1]] }));
def(O(OBJ.WATER_WHEEL, 'water_wheel', 'placed', true, 2, 0, 0, 0x8a6a40, { tool: 'any', drops: [['water_wheel', 1]] }));
def(O(OBJ.WINDMILL, 'windmill', 'placed', true, 2, 0, 0, 0x8a6a40, { tool: 'any', drops: [['windmill', 1]] }));
def(O(OBJ.ALTERNATOR, 'alternator', 'placed', true, 2, 0, 0, 0x6a6a7a, { tool: 'any', drops: [['alternator', 1]] }));
// A planted sapling grows into a tree (for resin groves); it can be pulled up again before then.
def(O(OBJ.SAPLING, 'sapling', 'placed', false, 1, 0, 0, 0x5a8a3a, { tool: 'any', drops: [['sapling', 1]], grow: { next: OBJ.TREE, secs: 480 } }));
def(O(OBJ.STORAGE_CORE, 'storage_core', 'placed', true, 3, 0, 0, 0x4a7ac0, { tool: 'any', drops: [['storage_core', 1]], light: 110 }));
def(O(OBJ.TERMINAL, 'terminal', 'placed', true, 2, 0, 0, 0x4a8ac0, { tool: 'any', drops: [['storage_terminal', 1]], light: 90 }));
def(O(OBJ.BRAZIER, 'brazier', 'ruin', true, 0, 0, 0, 0xe08030, { light: 190 }));
def(O(OBJ.PORTAL, 'portal', 'ruin', false, 0, 0, 0, 0x4a8ae0, { light: 220 }));
def(O(OBJ.BLOOD_ALTAR, 'blood_altar', 'placed', true, 3, 0, 0, 0x801828, { tool: 'any', drops: [['blood_altar', 1]], light: 120 }));
def(O(OBJ.BLOOD_RUNE, 'blood_rune', 'placed', false, 2, 0, 0, 0x902030, { tool: 'any', drops: [['blood_rune', 1]] }));
def(O(OBJ.RITUAL_STONE, 'ritual_stone', 'placed', true, 3, 0, 0, 0x5a2a3a, { tool: 'any', drops: [['master_ritual_stone', 1]] }));
def(O(OBJ.BLOOD_LAMP, 'blood_lamp', 'placed', false, 1, 0, 0, 0xe04040, { tool: 'any', light: 170 }));
def(O(OBJ.BLOOD_CAPSTONE, 'blood_capstone', 'placed', true, 3, 0, 0, 0xa02030, { tool: 'any', drops: [['blood_capstone', 1]], light: 90 }));
def(O(OBJ.COOKING_FIRE, 'cooking_fire', 'placed', true, 2, 0, 0, 0xe07030, { tool: 'any', drops: [['cooking_fire', 1]], light: 170 }));
// Crops: 4 stages each. The first three grow on the world clock; the ripe one waits to be picked.
CROPS.forEach((c, i) => {
  for (let st = 0; st < 4; st++) {
    const ripe = st === 3;
    def(O(cropObj(i, st), c.art[st], 'crop', false, ripe ? 1 : 0, ripe ? OBJ.SOIL : 0, 0, 0x6a9a3a, {
      tool: 'any',
      soil: true,
      ...(ripe ? {} : { grow: { next: cropObj(i, st + 1), secs: c.stageSecs } }),
    }));
  }
});

/** Object id -> machine kind. */
export const MACHINE_OF: Partial<Record<number, 'furnace' | 'generator' | 'sag_mill' | 'alloy' | 'still' | 'tap' | 'harvester' | 'alternator'>> = { 35: 'furnace', 36: 'generator', 37: 'sag_mill', 38: 'alloy', 82: 'still', 83: 'tap', 84: 'harvester', 87: 'alternator' };

/** What item pipes (OBJ.CONDUIT) join into one item network: storage, and machines that take or give
 *  items (server/machines.ts builds them; the client draws the joins). */
export const NET_OBJS = new Set<number>([OBJ.CONDUIT, OBJ.CHEST, OBJ.FURNACE, OBJ.GENERATOR, OBJ.SAG_MILL, OBJ.ALLOY, OBJ.STORAGE_CORE, OBJ.TERMINAL, OBJ.STILL, OBJ.RESIN_TAP, OBJ.HARVESTER]);
/** What energy cables join into one power network: generators and the machines that run on power. */
export const POWER_OBJS = new Set<number>([OBJ.CABLE, OBJ.GENERATOR, OBJ.SAG_MILL, OBJ.ALLOY, OBJ.STILL, OBJ.HARVESTER, OBJ.ALTERNATOR]);
/** Anything either kind of line touches (placing or removing one rebuilds the networks). */
export const LINE_OBJS = new Set<number>([...NET_OBJS, ...POWER_OBJS]);

export const objSolid = (o: number): boolean => OBJECTS[o]?.solid ?? false;
export const harvestable = (o: number): boolean => (OBJECTS[o]?.hits ?? 0) > 0;

/** Swing power of a held item against an object, or a reason it can't be harvested. */
export function harvestPower(o: number, held: { axe?: number; pick?: number } | null): number | string {
  const d = OBJECTS[o];
  if (!d || !d.hits) return 'Nothing to gather there.';
  const tier = d.tier ?? 1;
  if (d.tool === 'pick') {
    const p = held?.pick ?? 0;
    if (!p) return d.hand ? 0.5 : 'You need a pickaxe for that.';
    if (p < tier) return `That needs a better pickaxe (tier ${tier}).`;
    return p;
  }
  if (d.tool === 'axe') {
    const a = held?.axe ?? 0;
    if (a && a < tier) return `That needs a better axe (tier ${tier}).`;
    if (!a && tier > 1) return `That needs an axe (tier ${tier}).`;
    return a || 0.5; // bare hands work on ordinary trees, slowly
  }
  return 1;
}

// ---------------------------------------------------------------- rings

export type Ring = { tier: number; name: string; trees: number[]; ores: [number, number][] };

/** Ore weights per ring: [object, weight]. Plain rock is always common. */
export const RINGS: Ring[] = [
  { tier: 1, name: 'Greenwood', trees: [OBJ.TREE, OBJ.TREE, OBJ.BIRCH, OBJ.PINE], ores: [[OBJ.ROCK, 6], [OBJ.COPPER, 2], [OBJ.TIN, 2]] },
  { tier: 2, name: 'Fenlands', trees: [OBJ.FEN_TREE, OBJ.FEN_TREE, OBJ.TREE], ores: [[OBJ.ROCK, 5], [OBJ.IRON, 2], [OBJ.COAL, 2], [OBJ.COPPER, 0.5]] },
  { tier: 3, name: 'Highlands', trees: [OBJ.PINE, OBJ.PINE, OBJ.BIRCH], ores: [[OBJ.ROCK, 6], [OBJ.SILVER, 2], [OBJ.GOLD, 1.5], [OBJ.IRON, 1]] },
  { tier: 4, name: 'Ashlands', trees: [OBJ.BURNT_TREE], ores: [[OBJ.ROCK, 4], [OBJ.DARK_ORE, 2], [OBJ.OBSIDIAN, 2], [OBJ.COAL, 1]] },
];
